/*
 * <omosa-background-video>: muted looping video over a photo (snippets/background-video.liquid).
 * Nothing is downloaded before the page has loaded, so the photo stays the first thing shown
 * (LCP). No playback when the visitor limits animations (prefers-reduced-motion), saves data or
 * is on a 2G connection: the photo stays. Paused off screen to save battery; the pause button
 * keeps the visitor's choice.
 */
class OmosaBackgroundVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    this.button = this.querySelector('.background-video__toggle');
    this.pausedByVisitor = false;

    const connection = navigator.connection;
    const slow = connection?.saveData || /2g/.test(connection?.effectiveType || '');
    if (slow || matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.video.addEventListener('playing', () => this.classList.add('is-playing'), { once: true });
    this.button.addEventListener('click', () => this.toggle());

    if (document.readyState === 'complete') this.start();
    else addEventListener('load', () => this.start(), { once: true });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  start() {
    this.video.preload = 'auto';
    this.button.hidden = false;
    this.observer = new IntersectionObserver(([entry]) => {
      if (this.pausedByVisitor) return;
      if (entry.isIntersecting) this.play();
      else this.video.pause();
    });
    this.observer.observe(this);
  }

  play() {
    // Autoplay can still be refused (e.g. low power mode on iPhone): the photo stays, the button
    // offers to play.
    this.video.play().catch(() => this.showState(true));
    this.showState(false);
  }

  toggle() {
    this.pausedByVisitor = !this.video.paused;
    if (this.pausedByVisitor) {
      this.video.pause();
      this.showState(true);
    } else {
      this.play();
    }
  }

  showState(paused) {
    this.classList.toggle('is-paused', paused);
    this.button.setAttribute('aria-label', paused ? this.button.dataset.labelPlay : this.button.dataset.labelPause);
  }
}

customElements.define('omosa-background-video', OmosaBackgroundVideo);
